import { FC, useRef, useState } from 'react';
import { Field, Formik, Form, FormikProps } from 'formik';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';

import { CustomButton, FormTextField, PhoneInput } from '../../../../components';

import './ChecksAndPasswordFormGroup.scss';
import { ChevronIcon } from '../../../../assets/icons/ChevronIcon';
import { CheckIcon } from '../../../../assets/icons/CheckIcon';
import { AnimatePresence, motion } from 'motion/react';
import { loginApi } from '../../../../apis/auth';

import { authSignUpIn, authConfirmSignUpIn, type AuthMethod } from '../../../../apis/authCognito';

import { cognitoResetPasswordApi } from '../../../../apis/authCognito';
import { ICurrentUser } from '../../../../features/currentUser/currentUserSlice';
import { Notification } from '../../../../hooks/useNotification';
import { ToastContainer } from 'react-toastify';
import { TypeError } from '../../../../types/enums';

type LoginFormModel = {
  login: string; // phone OR email
  loginMethod: 'phone' | 'email';
};

type MFACodeFormModel = { mfaCode: string };

interface IChecksAndPasswordFormGroupProps {
  onSubmit?: (values: Omit<ICurrentUser, 'roles'>) => void;
}

export const ChecksAndPasswordFormGroup: FC<IChecksAndPasswordFormGroupProps> = ({ onSubmit }) => {
  const prefixValidation = 'validation-';
  const prefix = 'checks-';

  const { t } = useTranslation();

  const [loginValue, setLoginValue] = useState<string | null>(null);
  const [mfaCode, setMfaCode] = useState<number | null>(null);
  const [loginMethod, setLoginMethod] = useState<'phone' | 'email'>('phone');

  // ----- STEP 1: Login (phone/email) form -----
  const loginForm: LoginFormModel = {
    login: '',
    loginMethod: 'phone',
  };

  const loginSchema = Yup.object().shape({
    loginMethod: Yup.mixed<'phone' | 'email'>().oneOf(['phone', 'email']).required(),
    login: Yup.string().when('loginMethod', {
      is: 'email',
      then: (schema) =>
        schema
          .email(t(prefixValidation + 'emailInvalid'))
          .required(t(prefixValidation + 'emailRequired')),
      otherwise: (schema) => schema.required(t(prefixValidation + 'phoneRequired')),
    }),
  });

  const loginFormRef = useRef<FormikProps<LoginFormModel>>(null);

  const loginHandleForm = async ({ login, loginMethod }: LoginFormModel) => {
    try {
      const method: AuthMethod = loginMethod === 'email' ? 'email' : 'sms';

      if (method === 'email') {
        await authSignUpIn({
          email: login,
          method: 'email',
        });
      } else {
        await authSignUpIn({
          phone: login,
          method: 'sms',
        });
      }

      setLoginValue(login);
      // Show success notification
      Notification(
        TypeError.success,
        loginMethod === 'email' ? t(prefix + 'codeSentEmail') : t(prefix + 'codeSentSms')
      );
    } catch (e: any) {
      console.error(e);

      loginFormRef.current?.setFieldError(
        'login',
        loginMethod === 'phone' ? t(prefix + 'phoneIncorrect') : t(prefix + 'emailIncorrect')
      );
      setLoginValue(null);
    }
  };

  // ----- STEP 2: MFA form -----
  const mfaCodeForm: MFACodeFormModel = {
    mfaCode: '',
  };

  const mfaCodeSchema = Yup.object().shape({
    mfaCode: Yup.string()
      .required(t(prefixValidation + 'mfaCodeIncorrect'))
      .test('is-number', t(prefixValidation + 'mfaCodeIncorrect'), (value) => {
        if (!value || value.trim() === '') return false;
        return !isNaN(Number(value));
      }),
  });

  const mfaCodeFormRef = useRef<FormikProps<MFACodeFormModel>>(null);

  const mfaCodeHandleForm = async ({ mfaCode }: MFACodeFormModel) => {
    if (!loginValue) return;

    try {
      await authConfirmSignUpIn(mfaCode);
      setMfaCode(+mfaCode);
    } catch (e) {
      console.error(e);
      mfaCodeFormRef.current?.setFieldError('mfaCode', t(prefixValidation + 'mfaCodeIncorrect'));
      setMfaCode(null);
    }
  };

  // ----- STEP 3: Password form -----
  const passwordForm = {
    password: '',
    confirmPassword: '',
  };

  const passwordSchema = Yup.object().shape({
    password: Yup.string()
      .min(8, t(prefixValidation + 'passwordTooShort'))
      .max(24, t(prefixValidation + 'passwordTooLong'))
      .required(t(prefixValidation + 'passwordRequred')),
    confirmPassword: Yup.string()
      .required(t(prefixValidation + 'passwordRequred'))
      .oneOf([Yup.ref('password'), null], t(prefixValidation + 'passwordsMustMatch')),
  });

  return (
    <div className="checks-password-form-group">
      {/* STEP 1: Login (Phone / Email) + toggle + animation */}
      <Formik
        validationSchema={loginSchema}
        initialValues={loginForm}
        onSubmit={loginHandleForm}
        innerRef={loginFormRef}
        validateOnChange={false}
        validateOnBlur={true}
      >
        {({ values, setFieldValue, setFieldError, setErrors }) => (
          <>
            <div className="checks-password-form-group__method-toggle-one">
              <button
                type="button"
                className="checks-password-form-group__method-btn-one"
                onClick={() => {
                  const next = values.loginMethod === 'phone' ? 'email' : 'phone';

                  setFieldValue('loginMethod', next);
                  setFieldValue('login', '');
                  setFieldError('login', undefined);
                  setErrors({});
                  setLoginMethod(next); // Update state to trigger MFA label re-render

                  setLoginValue(null);
                  mfaCodeFormRef.current?.setValues(mfaCodeForm);
                  mfaCodeFormRef.current?.resetForm();
                  mfaCodeFormRef.current?.setTouched({ mfaCode: false });
                  mfaCodeFormRef.current?.setErrors({});
                  setMfaCode(null);
                }}
              >
                {values.loginMethod === 'phone'
                  ? t('signIn-changeToEmail')
                  : t('signIn-changeToPhone')}
              </button>
            </div>

            <Form
              className="checks-password-form-group__single-item"
              onChange={() => {
                if (loginValue) {
                  setLoginValue(null);
                  mfaCodeFormRef.current?.setValues(mfaCodeForm);
                  mfaCodeFormRef.current?.resetForm();
                  mfaCodeFormRef.current?.setTouched({
                    mfaCode: false,
                  });
                  mfaCodeFormRef.current?.setErrors({});
                  setMfaCode(null);
                }
              }}
            >
              <div className="checks-password-form-group__single-item_text-field">
                <div className="checks-password-form-group__login-wrapper">
                  <AnimatePresence mode="wait">
                    {values.loginMethod === 'phone' && (
                      <motion.div
                        key="phone"
                        initial={{ opacity: 0, translateY: -10 }}
                        animate={{ opacity: 1, translateY: 0 }}
                        exit={{ opacity: 0, translateY: 10 }}
                        transition={{ duration: 0.2 }}
                      >
                        <Field
                          name="login"
                          component={PhoneInput}
                          label={t(prefix + 'phonePlaceholder')}
                        />
                      </motion.div>
                    )}

                    {values.loginMethod === 'email' && (
                      <motion.div
                        key="email"
                        initial={{ opacity: 0, translateY: -10 }}
                        animate={{ opacity: 1, translateY: 0 }}
                        exit={{ opacity: 0, translateY: 10 }}
                        transition={{ duration: 0.2 }}
                      >
                        <div className="checks-password-form-group__email-wrapper">
                          <FormTextField
                            name="login"
                            className="!py-0"
                            label={t('signIn-emailPlaceholder')}
                          />
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </div>

              <div className="checks-password-form-group__single-item_submit-go">
                <CustomButton
                  disabled={!!loginValue}
                  btnText={t(prefix + 'phoneSubmit')}
                  type="submit"
                  variant="outline-dark"
                >
                  <ChevronIcon isClick={false} width="50px" height="25px" fill="#000" />
                </CustomButton>
              </div>
            </Form>
          </>
        )}
      </Formik>

      {/* STEP 2: MFA */}
      <Formik
        validationSchema={mfaCodeSchema}
        initialValues={mfaCodeForm}
        onSubmit={mfaCodeHandleForm}
        innerRef={mfaCodeFormRef}
        validateOnChange={false}
        validateOnBlur={false}
      >
        {({ setErrors }) => (
          <Form
            className="checks-password-form-group__single-item"
            onChange={() => {
              if (mfaCode !== null) {
                setMfaCode(null);
                setErrors({});
              }
            }}
          >
            <div className="checks-password-form-group__single-item_text-field">
              <FormTextField
                name="mfaCode"
                type="text"
                disabled={!loginValue}
                label={
                  loginMethod === 'email'
                    ? t(prefix + 'mfaCodePlaceholderEmail')
                    : t(prefix + 'mfaCodePlaceholder')
                }
              />
            </div>
            <div className="checks-password-form-group__single-item_submit">
              <CustomButton
                disabled={mfaCode !== null || !loginValue}
                btnText={t(prefix + 'mfaCodeSubmit')}
                type="submit"
                variant={mfaCode !== null ? 'success' : 'outline-dark'}
                className={mfaCode !== null ? 'checks-password-form-group__verify-success' : ''}
              >
                <CheckIcon w="50px" h="25px" f={mfaCode !== null ? '#FFFFFF' : '#000'} />
              </CustomButton>
            </div>
          </Form>
        )}
      </Formik>

      {/* STEP 3: Password */}
      <Formik
        validationSchema={passwordSchema}
        initialValues={passwordForm}
        onSubmit={async ({ password }, formikHelpers) => {
          if (!loginValue || mfaCode === null) return;

          try {
            // 1) Ask backend to change password
            await cognitoResetPasswordApi(password);

            // 2) Immediately perform a normal login with the new password
            const user = await loginApi({
              login: loginValue, // phone or email
              password,
            });

            if (!user.accessToken || !user.refreshToken) {
              console.error('Login after reset returned missing tokens', user);
              formikHelpers.setFieldError(
                'confirmPassword',
                t('checks-resetPasswordFailed', 'Something went wrong, please try again.')
              );
              return;
            }

            // 3) Bubble the login response up so parent can call setUser
            onSubmit?.({
              accessToken: user.accessToken,
              refreshToken: user.refreshToken,
            });
          } catch (error: any) {
            console.error(error);
            formikHelpers.setFieldError(
              'confirmPassword',
              t('checks-resetPasswordFailed', 'Something went wrong, please try again.')
            );
          }
        }}
      >
        <Form>
          <div className="checks-password-form-group__double-item">
            <FormTextField
              name="password"
              type="password"
              disabled={!loginValue || mfaCode === null}
              label={t(prefix + 'passwordPlaceholder')}
            />
          </div>
          <div className="checks-password-form-group__double-item">
            <FormTextField
              name="confirmPassword"
              type="password"
              disabled={!loginValue || mfaCode === null}
              label={t(prefix + 'passwordConfirmPlaceholder')}
            />

            <div className="checks-password-form-group__double-item_submit">
              <CustomButton btnText={t(prefix + 'submit')} type="submit" />
            </div>
          </div>
        </Form>
      </Formik>
      <ToastContainer />
    </div>
  );
};
